// useNativeFileShare.js - Shared native/web share-sheet invocation.
//
// Wraps the Capacitor Share plugin (native apps) and the Web Share API
// (browsers/PWA) behind one function so chart sharing, session-tile sharing,
// and day-tile sharing don't each re-implement the same platform branching
// that previously lived only in PastSessions.handleShareChart.
import { useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import logger from '../utils/logger';

/** Reads a File/Blob into a base64 string (without the data: prefix). */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const base64 = String(result).split(',').pop();
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Writes File objects to the app's cache dir and returns their file:// URIs. */
async function filesToNativeUris(files) {
  const uris = [];
  for (const file of files) {
    try {
      const base64 = await fileToBase64(file);
      const path = `pista-share-${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`;
      const result = await Filesystem.writeFile({
        path,
        data: base64,
        directory: Directory.Cache,
      });
      if (result?.uri) {
        uris.push(result.uri);
      }
    } catch (err) {
      logger.warn('Failed to stage file for native share:', err);
    }
  }
  return uris;
}

/**
 * @returns {{ shareNative: (payload: { title?: string, text?: string, url?: string, files?: File[] }) => Promise<'shared'|'cancelled'|'unsupported'|'error'> }}
 */
export function useNativeFileShare() {
  const shareNative = useCallback(async ({ title, text, url, files = [] } = {}) => {
    const isNativeMobile = Capacitor.isNativePlatform();

    if (isNativeMobile) {
      try {
        const fileUris = files.length > 0 ? await filesToNativeUris(files) : undefined;
        // Many Android apps (e.g. Instagram, Messages) reject a media intent that
        // also contains EXTRA_TEXT, so share files with no text body.
        await Share.share(files.length > 0 ? { title, files: fileUris } : { title, text, url });
        return 'shared';
      } catch (err) {
        if (err?.message === 'Share canceled' || err?.name === 'AbortError') {
          return 'cancelled';
        }
        logger.error('Native Share failed:', err);
        return 'error';
      }
    }

    if (navigator?.share) {
      try {
        const data = { title, text, url };
        if (files.length > 0) {
          if (typeof navigator.canShare === 'function' && !navigator.canShare({ files })) {
            // Browser reports it cannot share files; let the UI fall back to copy.
            return 'unsupported';
          }
          data.files = files;
        }
        await navigator.share(data);
        return 'shared';
      } catch (err) {
        if (err?.name === 'AbortError') {
          return 'cancelled';
        }
        if (['NotAllowedError', 'TypeError', 'DataError'].includes(err?.name)) {
          // File sharing may be unsupported even though canShare passed; let UI copy.
          return 'unsupported';
        }
        logger.error('Web Share API failed:', err);
        return 'error';
      }
    }

    return 'unsupported';
  }, []);

  return { shareNative };
}

export default useNativeFileShare;
